import type {
  FeatureServiceBinder,
  FeatureServiceProviderDefinition,
  SharedFeatureService,
} from '@feature-hub/core';
import {
  readPriceTypeFromStorage,
  writePriceTypeToStorage,
} from './utils/storage.js';

export interface PricePreferencesProviderV1 {
  getPriceType: () => PriceTypeV1;
  setPriceType: (priceType: PriceTypeV1) => void;
  getShowPrices: () => boolean;
  setShowPrices: (showPrices: boolean) => void;

  subscribe: (
    listener: () => void,
    options?: {readonly signal: AbortSignal},
  ) => () => void;
}

export type PriceTypeV1 = 'private' | 'commercial';

export interface SharedPricePreferencesProvider extends SharedFeatureService {
  readonly '1.0.0': FeatureServiceBinder<PricePreferencesProviderV1>;
}

export interface PricePreferencesProviderOptions {
  readonly initialPriceType: PriceTypeV1;
  readonly initialShowPrices: boolean;
}

export function definePricePreferencesProvider(
  options: PricePreferencesProviderOptions,
): FeatureServiceProviderDefinition<SharedPricePreferencesProvider> {
  const {initialPriceType, initialShowPrices} = options;

  let priceType: PriceTypeV1 = initialPriceType;
  const priceTypeFromStorage = readPriceTypeFromStorage();

  if (!priceTypeFromStorage) {
    writePriceTypeToStorage(initialPriceType);
  } else {
    priceType = priceTypeFromStorage;
  }
  let showPrices = initialShowPrices;
  const listeners = new Set<() => void>();

  const notifyListeners = () => {
    for (const listener of listeners) {
      listener();
    }
  };

  return {
    id: `price-preferences-provider`,

    create: () => {
      return {
        '1.0.0': () => ({
          featureService: {
            getPriceType: () => priceType,

            setPriceType: (newPriceType) => {
              if (priceType !== newPriceType) {
                writePriceTypeToStorage(newPriceType);
                priceType = newPriceType;
                notifyListeners();
              }
            },

            getShowPrices: () => showPrices,

            setShowPrices: (newShowPrices) => {
              if (showPrices !== newShowPrices) {
                showPrices = newShowPrices;
                notifyListeners();
              }
            },

            subscribe: (listener, listenerOptions) => {
              listeners.add(listener);

              const unsubscribe = () => listeners.delete(listener);

              listenerOptions?.signal.addEventListener(`abort`, unsubscribe);

              return unsubscribe;
            },
          },
        }),
      };
    },
  };
}
